import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Card } from '../ui/card'
import { useToast } from '../../hooks/use-toast'
import { getThemeClass } from '../../lib/theme'
import { Alert, AlertDescription } from '../ui/alert'
import { supabase } from '../../lib/supabase'
import { Session } from '@supabase/supabase-js'
import { useAuth } from '../../lib/auth.context'
import { env } from '../../lib/env'
import { getUserProfile } from '../../lib/api/api'
import { ROUTES } from 'src/config/routes'
import { InputOTP } from '../input-otp'

interface TOTPVerifyProps {
  onComplete?: () => void
  redirectTo?: string
}

export function TOTPVerify({ onComplete, redirectTo }: TOTPVerifyProps) {
  const [verifyCode, setVerifyCode] = useState('')
  const [formError, setFormError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [factorId, setFactorId] = useState<string>('')
  const navigate = useNavigate()
  const { toast } = useToast()
  const { setIsTOTPVerified } = useAuth()

  useEffect(() => {
    const getTOTPFactor = async () => {
      try {
        const { data: factors } = await supabase.auth.mfa.listFactors()
        if (!factors?.totp?.length) throw new Error('No TOTP factors found')

        const totpFactor = factors.totp[0]
        if (!totpFactor) throw new Error('No verified TOTP factor found')
        console.log('[TOTPVerify] totpFactor:', totpFactor)
        setFactorId(totpFactor.id)
      } catch (error: any) {
        const errorMessage = error.message || 'Failed to get TOTP factor'
        setFormError(errorMessage)
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        })
      }
    }

    getTOTPFactor()
  }, [toast])

  const handleVerify = async (e?: React.FormEvent) => {
    e?.preventDefault()
    setFormError('')
    setIsLoading(true)

    try {
      if (!factorId) throw new Error('TOTP factor not found')

      console.log('[TOTPVerify] factorId:', factorId)
      const { data: challenge, error } = await supabase.auth.mfa.challenge({
        factorId: factorId,
      })

      if (error) throw error

      const { data, error: verifyError } = await supabase.auth.mfa.verify({
        factorId: factorId,
        challengeId: challenge.id,
        code: verifyCode,
      })

      if (verifyError) throw verifyError

      console.log('[TOTPVerify] MFA verification successful, data:', {
        hasAccessToken: !!data.access_token,
        hasRefreshToken: !!data.refresh_token,
        userId: data.user?.id
      })

      // Create a new session object from the response
      const session: Session = {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        expires_in: data.expires_in,
        expires_at: Math.floor(Date.now() / 1000) + data.expires_in,
        token_type: data.token_type,
        user: data.user,
      }

      console.log('[TOTPVerify] Setting session with MFA verification complete')

      // Set the new session with aal2 token
      const { error: sessionError } = await supabase.auth.setSession(session)
      
      if (sessionError) {
        console.error('[TOTPVerify] Error setting session:', sessionError)
        throw sessionError
      }

      // Verify the session was set correctly
      const { data: currentSession } = await supabase.auth.getSession()
      console.log('[TOTPVerify] Current session after setSession:', {
        hasSession: !!currentSession.session,
        userId: currentSession.session?.user?.id
      })

      // Manually set TOTP verification state to ensure it's set immediately
      console.log('[TOTPVerify] Manually setting TOTP verification to true')
      setIsTOTPVerified(true)

      toast({
        title: 'Success',
        description: 'TOTP verification successful',
      })

      // Get user profile to check role
      const userProfile = await getUserProfile()
      const isPartnerUser = userProfile.userRole?.startsWith('entity_')

      if (onComplete) {
        onComplete()
      }

      // Add a small delay to ensure auth context is updated before redirect
      setTimeout(() => {
        console.log('[TOTPVerify] Proceeding with redirect after delay')
        // Since this is partner-ui, redirect to the dashboard
        if (redirectTo?.startsWith('http')) {
          // Handle any external URLs
          window.location.replace(redirectTo)
        } else if (redirectTo) {
          // Internal route navigation
          navigate(redirectTo)
        } else {
          // Default internal route for partner users
          navigate(ROUTES.ADMIN.DASHBOARD)
        }
      }, 200)
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to verify TOTP'
      setFormError(errorMessage)
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex flex-col items-center justify-center">
      <div className="flex items-center gap-2 mb-16">
        <img src="/logo.png" alt="Logo" className="w-[170px] h-[40px]" />
      </div>
      <form onSubmit={handleVerify}>
        <Card className="w-full max-w-lg border border-white rounded-3xl bg-white/50 p-8 justify-center">
          <div className="flex flex-col w-full gap-6 items-center">
            <div className="flex flex-col items-center mx-8 gap-2">
              <h1 className="font-semibold text-lavender-900 text-[40px] leading-[52px]">2FA Verification</h1>
              <div className="flex flex-col">
                <p className="font-[400] text-[14px] text-[#666666]">
                  Enter the 6-digit code from your authentication app
                </p>
                <p className="font-[400] text-[14px] text-[#666666]">
                  (Google Authenticator, Microsoft Authenticator, etc).
                </p>
              </div>
            </div>

            <div className="flex flex-col items-center gap-8 w-full">
              <InputOTP length={6} onChange={(value) => setVerifyCode(value)} />

              {formError && (
                <Alert variant="destructive" className="mt-4">
                  <AlertDescription>{formError}</AlertDescription>
                </Alert>
              )}

              <Button type="submit" disabled={isLoading} className="w-full rounded-3xl bg-[#354297] hover:bg-[#354297]">
                {isLoading ? 'Verifying...' : 'Verify Code'}
              </Button>
            </div>
          </div>
        </Card>
      </form>
    </div>
  )
}
